import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import moment from "moment";
import { getUserDirectory } from "../../api/userAPI";
import { getAwards, createAward, deleteAward } from "../../api/employeeOfMonthAPI";
import useSnackbar from "../../hooks/useSnackbar";
import {
  AwardChip,
  BRAND_GRADIENT,
  GOLD_GRADIENT,
  MANAGER_ROLES,
  UserAvatar,
  formatAwardDate,
  formatMonth,
  getCurrentAward,
  birthdayLabel,
  daysUntilBirthday,
} from "../Profile/profileUi";

const UPCOMING_BIRTHDAY_DAYS = 30;

// Today's birthdays get a pink highlight; the next 30 days are listed after them.
const BirthdaysSection = ({ people, onOpen }) => {
  const list = people
    .map((p) => ({ ...p, daysLeft: daysUntilBirthday(p.birthday) }))
    .filter((p) => p.daysLeft !== null && p.daysLeft <= UPCOMING_BIRTHDAY_DAYS)
    .sort((a, b) => a.daysLeft - b.daysLeft);
  if (list.length === 0) return null;

  return (
    <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 4, border: "1px solid #fbcfe8", bgcolor: "#fff7fb" }}>
      <Typography variant="h6" sx={{ fontWeight: 800, color: "#9d174d", mb: 2 }}>
        🎂 Birthdays
      </Typography>
      <Box sx={{ display: "flex", gap: 1.5, overflowX: "auto", pb: 0.5 }}>
        {list.map((p) => {
          const isToday = p.daysLeft === 0;
          return (
            <Box
              key={p._id}
              onClick={() => onOpen(p._id)}
              sx={{
                flexShrink: 0,
                minWidth: 190,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.5,
                borderRadius: 3,
                cursor: "pointer",
                bgcolor: "#fff",
                border: isToday ? "2px solid #ec4899" : "1px solid #fce7f3",
                background: isToday ? "linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%)" : "#fff",
                boxShadow: isToday ? "0 8px 20px rgba(236,72,153,.2)" : "none",
                transition: "transform .2s",
                "&:hover": { transform: "translateY(-2px)" },
              }}
            >
              <UserAvatar name={p.fullName} size={42} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700 }} noWrap>
                  {p.fullName}
                </Typography>
                <Typography variant="body2" sx={{ color: isToday ? "#be185d" : "text.secondary", fontWeight: isToday ? 800 : 500 }}>
                  {isToday ? "🎉 Today!" : p.daysLeft === 1 ? `Tomorrow · ${birthdayLabel(p.birthday)}` : `${birthdayLabel(p.birthday)} · in ${p.daysLeft} days`}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

const Spotlight = ({ award, onOpen }) => (
  <Paper
    elevation={0}
    onClick={onOpen}
    sx={{
      cursor: "pointer",
      mb: 3,
      p: { xs: 2.5, md: 3.5 },
      borderRadius: 4,
      background: GOLD_GRADIENT,
      color: "#3d2700",
      position: "relative",
      overflow: "hidden",
      boxShadow: "0 14px 34px rgba(226,169,59,.35)",
      transition: "transform .2s",
      "&:hover": { transform: "translateY(-2px)" },
    }}
  >
    <EmojiEventsIcon sx={{ position: "absolute", right: { xs: -20, md: 24 }, top: -10, fontSize: { xs: 140, md: 190 }, opacity: 0.18 }} />
    <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5} alignItems={{ xs: "flex-start", sm: "center" }} sx={{ position: "relative" }}>
      <UserAvatar name={award.user?.fullName} size={88} ring sx={{ border: "4px solid #fff7dd" }} />
      <Box>
        <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: 1.5 }}>
          {award.title || "Employee of the Month"} · {formatMonth(award.month)}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
          {award.user?.fullName}
        </Typography>
        <Typography sx={{ fontWeight: 600, opacity: 0.85 }}>
          {[award.user?.permission, award.user?.department].filter(Boolean).join(" · ")}
        </Typography>
        {award.citation && (
          <Typography sx={{ mt: 1, fontStyle: "italic", maxWidth: 680 }}>“{award.citation}”</Typography>
        )}
      </Box>
    </Stack>
  </Paper>
);

const PersonCard = ({ person, isCurrentWinner, onOpen }) => (
  <Paper
    elevation={0}
    onClick={onOpen}
    sx={{
      cursor: "pointer",
      p: 2.5,
      borderRadius: 4,
      textAlign: "center",
      border: isCurrentWinner ? "2px solid #e2a93b" : "1px solid #eef0f6",
      position: "relative",
      transition: "all .2s",
      "&:hover": { transform: "translateY(-3px)", boxShadow: "0 12px 28px rgba(102,126,234,.15)" },
    }}
  >
    {person.awardMonths.length > 0 && (
      <Tooltip title={`Employee of the Month: ${person.awardMonths.map(formatMonth).join(", ")}`}>
        <Box
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            px: 1,
            py: 0.25,
            borderRadius: 999,
            background: GOLD_GRADIENT,
            color: "#4a3000",
            fontSize: 12,
            fontWeight: 800,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <EmojiEventsIcon sx={{ fontSize: 14 }} />
          {person.awardMonths.length}
        </Box>
      </Tooltip>
    )}
    {daysUntilBirthday(person.birthday) === 0 && (
      <Tooltip title="Birthday today!">
        <Box sx={{ position: "absolute", top: 10, left: 12, fontSize: 20 }}>🎂</Box>
      </Tooltip>
    )}
    <UserAvatar name={person.fullName} size={72} sx={{ mx: "auto", mb: 1.5 }} />
    <Typography sx={{ fontWeight: 800, color: "#1f2937" }} noWrap>
      {person.fullName}
    </Typography>
    <Typography variant="body2" color="text.secondary" noWrap>
      {person.role}
    </Typography>
    <Box sx={{ minHeight: 28, mt: 1 }}>
      {isCurrentWinner ? (
        <AwardChip />
      ) : (
        person.department && <Chip label={person.department} size="small" sx={{ bgcolor: "#f3e8ff", color: "#6b21a8", fontWeight: 600 }} />
      )}
    </Box>
  </Paper>
);

const AnnounceDialog = ({ open, onClose, people, takenMonths, onSubmit }) => {
  const [form, setForm] = useState({ user: null, month: moment().format("YYYY-MM"), announcedAt: moment().format("YYYY-MM-DD"), citation: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setForm({ user: null, month: moment().format("YYYY-MM"), announcedAt: moment().format("YYYY-MM-DD"), citation: "" });
  }, [open]);

  const monthTaken = takenMonths.includes(form.month);
  const validUntil = form.announcedAt ? moment(form.announcedAt).add(29, "days").format("DD MMM YYYY") : "";

  const submit = async () => {
    setSubmitting(true);
    try {
      await onSubmit({ user: form.user._id, month: form.month, announcedAt: form.announcedAt, citation: form.citation });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>🏆 Announce Employee of the Month</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Autocomplete
            options={people}
            value={form.user}
            onChange={(_, value) => setForm((f) => ({ ...f, user: value }))}
            getOptionLabel={(o) => o.fullName || ""}
            isOptionEqualToValue={(o, v) => o._id === v._id}
            renderOption={(props, o) => (
              <li {...props} key={o._id}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <UserAvatar name={o.fullName} size={30} />
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>{o.fullName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[o.role, o.department].filter(Boolean).join(" · ")}
                    </Typography>
                  </Box>
                </Stack>
              </li>
            )}
            renderInput={(params) => <TextField {...params} label="Employee" />}
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Award Month"
              type="month"
              value={form.month}
              onChange={(e) => setForm((f) => ({ ...f, month: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
              error={monthTaken}
              helperText={monthTaken ? "Already announced for this month" : " "}
            />
            <TextField
              label="Announcement Date"
              type="date"
              value={form.announcedAt}
              onChange={(e) => setForm((f) => ({ ...f, announcedAt: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText={validUntil ? `Reward leave valid till ${validUntil}` : " "}
            />
          </Stack>
          <TextField
            label="Citation (why they won)"
            multiline
            rows={3}
            value={form.citation}
            onChange={(e) => setForm((f) => ({ ...f, citation: e.target.value }))}
            fullWidth
          />
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "#fffbeb", border: "1px solid #fde68a", fontSize: 14, color: "#78350f" }}>
            The winner gets <b>1 extra paid leave</b> valid for <b>30 days</b> from the announcement date, a Recognition award on
            their profile, and everyone receives an announcement.
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={submitting || !form.user || !form.month || !form.announcedAt || monthTaken}
          sx={{ background: GOLD_GRADIENT, color: "#3d2700", fontWeight: 800 }}
        >
          {submitting ? "Announcing..." : "Announce"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const People = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.tokens) || {};
  const isManager = MANAGER_ROLES.includes(user?.permission);
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const [people, setPeople] = useState([]);
  const [awards, setAwards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [department, setDepartment] = useState("");
  const [announceOpen, setAnnounceOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dirRes, awardRes] = await Promise.all([getUserDirectory(), getAwards()]);
      setPeople(dirRes.data?.data || []);
      setAwards(awardRes.data?.data || []);
    } catch (err) {
      showSnackbar(err.response?.data?.message || "Failed to load directory", "error");
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const currentAward = getCurrentAward(awards);
  const currentWinnerId = currentAward?.user?._id;

  const departments = useMemo(
    () => [...new Set(people.map((p) => p.department).filter(Boolean))].sort(),
    [people]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return people.filter(
      (p) =>
        (!department || p.department === department) &&
        (!q || [p.fullName, p.email, p.role, p.department].some((v) => v?.toLowerCase().includes(q)))
    );
  }, [people, search, department]);

  const handleAnnounce = async (payload) => {
    try {
      await createAward(payload);
      showSnackbar("Employee of the Month announced! 🎉", "success");
      setAnnounceOpen(false);
      load();
    } catch (err) {
      showSnackbar(err.response?.data?.message || "Failed to announce", "error");
    }
  };

  const handleDelete = async (award) => {
    if (!window.confirm(`Remove ${award.user?.fullName}'s award for ${formatMonth(award.month)}?`)) return;
    try {
      await deleteAward(award._id);
      showSnackbar("Award removed", "info");
      load();
    } catch (err) {
      showSnackbar(err.response?.data?.message || "Failed to remove award", "error");
    }
  };

  const openProfile = (id) => navigate(id === user?._id ? "/profile" : `/profile/${id}`);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, background: BRAND_GRADIENT, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            People
          </Typography>
          <Typography color="text.secondary">Get to know your colleagues</Typography>
        </Box>
        {isManager && (
          <Button
            variant="contained"
            startIcon={<CampaignOutlinedIcon />}
            onClick={() => setAnnounceOpen(true)}
            sx={{ background: GOLD_GRADIENT, color: "#3d2700", fontWeight: 800, borderRadius: 2, textTransform: "none" }}
          >
            Announce Employee of the Month
          </Button>
        )}
      </Stack>

      {loading ? (
        <Box sx={{ display: "grid", placeItems: "center", minHeight: "40vh" }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {currentAward && <Spotlight award={currentAward} onOpen={() => openProfile(currentWinnerId)} />}

          <BirthdaysSection people={people} onOpen={openProfile} />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3 }}>
            <TextField
              placeholder="Search by name, email, role..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              sx={{ flex: 1, bgcolor: "#fff", borderRadius: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField select size="small" label="Department" value={department} onChange={(e) => setDepartment(e.target.value)} sx={{ minWidth: 200, bgcolor: "#fff" }}>
              <MenuItem value="">All departments</MenuItem>
              {departments.map((d) => (
                <MenuItem key={d} value={d}>
                  {d}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", mb: 5 }}>
            {filtered.map((p) => (
              <PersonCard key={p._id} person={p} isCurrentWinner={p._id === currentWinnerId} onOpen={() => openProfile(p._id)} />
            ))}
            {filtered.length === 0 && <Typography color="text.secondary">No colleagues match your search.</Typography>}
          </Box>

          {awards.length > 0 && (
            <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 4, border: "1px solid #f3e3b5", bgcolor: "#fffdf6" }}>
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
                <EmojiEventsIcon sx={{ color: "#e2a93b" }} />
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#4a3000" }}>
                  Hall of Fame
                </Typography>
              </Stack>
              <Stack spacing={1}>
                {awards.map((a) => (
                  <Box
                    key={a._id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: "#fff",
                      border: "1px solid #f6ead0",
                      cursor: "pointer",
                      "&:hover": { borderColor: "#e2a93b" },
                    }}
                    onClick={() => a.user?._id && openProfile(a.user._id)}
                  >
                    <Box sx={{ width: 110, flexShrink: 0, fontWeight: 800, color: "#a16207" }}>{formatMonth(a.month)}</Box>
                    <UserAvatar name={a.user?.fullName} size={36} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700 }} noWrap>
                        {a.user?.fullName || "Former employee"}
                      </Typography>
                      {a.citation && (
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {a.citation}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "none", sm: "block" } }}>
                      {formatAwardDate(a.announcedAt)}
                    </Typography>
                    {isManager && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(a);
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}
        </>
      )}

      {isManager && (
        <AnnounceDialog
          open={announceOpen}
          onClose={() => setAnnounceOpen(false)}
          people={people}
          takenMonths={awards.map((a) => a.month)}
          onSubmit={handleAnnounce}
        />
      )}
      <SnackbarComponent />
    </Container>
  );
};

export default People;
